import {
  allServiceSegments,
  Brochure,
  BrochureDownloadItem,
  cleanroomServiceSegments,
  Language,
  Market,
  ServiceSegmentsForCRM,
  ServiceSegmentType,
} from '@mewa/types';

import { localeIncludesServiceSegment } from '../../../../libs/config';
import t from '../../../../libs/translate';
import { CantoImage } from '../../../components/strapi/canto-image';
import { jpgPath } from '../../../helpers/asset-factory';
import cleanRoomImage from '../../../images/mewa-cleanroom-image.jpg';
import { mobileImageHeight, mobileImageWidth } from '../helper';
import { ConversionTranslations } from '../translations-query';

export type BrochureNamesAndImages = Record<
  Brochure,
  { name: string; imageSrc: string }
>;

export type ServiceSegments = {
  nodes: {
    name: string;
    key: ServiceSegmentType;
    assets: {
      default: string;
    };
  }[];
};

export type BrochureInfo = {
  key: Brochure;
  fileCount: number;
  name: string;
  imageSrc: string;
};

export type BrochureFile = {
  fields: {
    assetUrl: string;
  };
  sizeInKiloBytes: number;
  extension: string;
};

export type BrochureData = {
  collectionUuid?: string | null;
  file: BrochureFile;
  title?: string | null;
};

export type BrochureDownloads = {
  brandCatalog?: BrochureFile | null;
  sideImage: CantoImage;
  brandCatalogImage: CantoImage;
} & Record<ServiceSegmentsForCRM, BrochureData[]>;

const brandCatalogCantoParameters = '-C823x1080%2C857%2C0';

export const getBrochuresNamesAndImages = (
  translations: ConversionTranslations,
  serviceSegments: ServiceSegments,
  brandCatalogImage: CantoImage,
  market: Market,
  lang: Language,
): BrochureNamesAndImages =>
  Object.fromEntries([
    [
      'brandCatalog',
      {
        name: t(translations, 'brochures_download_form_brand_catalog'),
        imageSrc: jpgPath(
          brandCatalogImage.cantoDesktopImage,
          1720,
          968,
          brandCatalogCantoParameters,
        ),
      },
    ],
    // The following is a temporary solution until Cleanroom Segment is managed in PIM.
    ...(showCleanroomBrochure(market, lang)
      ? [
          [
            'cleanroom',
            {
              name: t(translations, 'brochures_download_form_cleanroom'),
              imageSrc: cleanRoomImage, // Imported image
            },
          ],
        ]
      : []),
    ...serviceSegments.nodes
      .filter((s) => !cleanroomServiceSegments.includes(s.key))
      .map(({ key, name, assets }) => [
        key,
        { name: name, imageSrc: jpgPath(assets.default, 1720, 968) },
      ]),
  ]);

const showCleanroomBrochure = (market: Market, lang: Language) =>
  cleanroomServiceSegments.some((cS) =>
    localeIncludesServiceSegment(cS, market, lang),
  );

export const prepareBrochureSelection = (
  serviceSegment: ServiceSegmentType,
): ServiceSegmentsForCRM =>
  cleanroomServiceSegments.includes(serviceSegment)
    ? 'cleanroom'
    : (serviceSegment as ServiceSegmentsForCRM);

export const allBrochures: Brochure[] = [
  ...allServiceSegments.map((s) => prepareBrochureSelection(s)),
  'brandCatalog',
];

export const getBrochuresCheckboxes = (
  brochuresWithFiles: BrochureDownloads,
  brochureNamesAndImages: BrochureNamesAndImages,
): BrochureInfo[] =>
  Object.entries(brochureNamesAndImages)
    .map(([key, brochure]) => {
      const brochureFiles = brochuresWithFiles[key as Brochure];

      return {
        key: key as Brochure,
        fileCount: Array.isArray(brochureFiles) ? brochureFiles.length : 1,
        name: brochure.name,
        imageSrc: brochure.imageSrc,
      };
    })
    .filter((b) => b.fileCount > 0);

export const getBrochuresHeaderCantoImage = (cantoImage: CantoImage) => ({
  desktop: jpgPath(cantoImage.cantoDesktopImage, 1920 * 1.5, 568 * 1.5),
  mobile: jpgPath(
    cantoImage.cantoMobileImage ?? cantoImage.cantoDesktopImage,
    mobileImageWidth * 3,
    mobileImageHeight * 3,
  ),
});

export type CollectionImage = {
  uuid: string;
  name: string;
  assets: {
    emotion: string;
  };
};

export const getBrochuresURLs = (
  selectedBrochures: Brochure[],
  brochuresData: BrochureNamesAndImages,
  strapiBrochuresDownload: BrochureDownloads,
  collectionImages: CollectionImage[],
): BrochureDownloadItem[] =>
  selectedBrochures
    .map((brochureKey) => {
      const label = brochuresData[brochureKey].name;
      const brochureData = strapiBrochuresDownload[brochureKey];

      if (!brochureData) {
        return null;
      }

      const isBrandCatalog = !Array.isArray(brochureData);

      if (isBrandCatalog) {
        const brandCatalogThumbnail = jpgPath(
          strapiBrochuresDownload.brandCatalogImage.cantoDesktopImage,
          640,
          840,
          brandCatalogCantoParameters,
        );

        return {
          label,
          key: brochureKey,
          files: [
            {
              thumbnail: brandCatalogThumbnail,
              url: brochureData.fields.assetUrl,
              sizeInKiloBytes: brochureData.sizeInKiloBytes,
              extension: brochureData.extension.substring(1),
              name: label,
            },
          ],
        };
      } else {
        return {
          label,
          key: brochureKey,
          files: brochureData.map((brochureFile) => {
            const collection = collectionImages.find(
              (collection) => collection.uuid === brochureFile.collectionUuid,
            );

            const thumbnail = collection?.assets.emotion
              ? jpgPath(collection.assets.emotion, 640, 840)
              : // service segment image is the fallback image if no collection is found or the collection has no emotion image
                brochuresData[brochureKey].imageSrc;

            return {
              thumbnail,
              url: brochureFile.file.fields.assetUrl,
              sizeInKiloBytes: brochureFile.file.sizeInKiloBytes,
              extension: brochureFile.file.extension.substring(1),
              name: brochureFile.title || collection?.name || label,
            };
          }),
        };
      }
    })
    .filter((items) => items !== null);
